import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
/* The prebuilt browser bundle, not the package entry. The entry pulls in
   `eventsource`, which calls Node's `util.inherits` and throws
   "util.inherits is not a function" in a browser — killing the module before a
   socket is ever opened. The user app imports this same path; that was a
   deliberate choice, not an accident. */
// @ts-expect-error - the dist bundle ships no types of its own.
import SockJS from 'sockjs-client/dist/sockjs';
import { API_BASE_URL } from '../config/env';
import { getAccessToken } from './storage/authTokens';
import { getCustomerAuthUser } from './storage/customerFlow';

/**
 * STOMP-over-SockJS client for live trip updates.
 *
 * Ported from the user app's `websocket.service.ts`. The transport is the same
 * on both platforms — the app imports `sockjs-client/dist/sockjs`, which is the
 * browser build — so this is a port of connection *policy*, not of protocol.
 *
 * Auth travels two ways, matching the app exactly:
 *   1. the access token as a `?token=` query parameter on the SockJS URL, and
 *   2. `Authorization` / `X-Principal-*` in the STOMP CONNECT frame.
 * The CONNECT frame is protocol payload rather than HTTP headers, so SockJS's
 * inability to set handshake headers never applies.
 *
 * NOTE FOR THE BACKEND TEAM: a JWT in a query string is recorded by proxy and
 * server access logs in a way an `Authorization` header is not. That is the
 * backend's contract and the app already depends on it, so it is not changed
 * here — but it is worth revisiting.
 */

export interface TripSocketHandlers {
  onTripStatus: (payload: Record<string, unknown>) => void;
  onDriverLocation: (payload: Record<string, unknown>) => void;
  onConnectionChange?: (connected: boolean) => void;
}

/** `https://host/base` → `https://host/base/messaging-service`, path preserved. */
function messagingBaseUrl(): string {
  const base = String(API_BASE_URL ?? '').trim();
  if (!base) throw new Error('Missing API base URL for WebSocket construction');

  const parsed = new URL(base.replace(/^ws(s)?:/i, (m) => (/^wss/i.test(m) ? 'https:' : 'http:')));
  const path = parsed.pathname.replace(/\/+$/, '');
  const messagingPath = path.endsWith('/messaging-service') ? path : `${path}/messaging-service`;

  return `${parsed.origin}${messagingPath}`;
}

function sockJsUrl(): string {
  const bearer = getAccessToken('customer') ?? '';
  const token = bearer.startsWith('Bearer ') ? bearer.slice(7) : bearer;

  return `${messagingBaseUrl()}/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;
}

function connectHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const user = getCustomerAuthUser() as { id?: string | number } | null;
  const bearer = getAccessToken('customer');

  if (user?.id != null) headers['X-Principal-Id'] = String(user.id);
  headers['X-Principal-Type'] = 'USER';
  if (bearer) headers.Authorization = bearer.startsWith('Bearer ') ? bearer : `Bearer ${bearer}`;

  return headers;
}

function parseBody(message: IMessage): Record<string, unknown> | null {
  try {
    return JSON.parse(message.body) as Record<string, unknown>;
  } catch {
    /* A malformed frame must not take down the whole tracking view. */
    return null;
  }
}

/**
 * Subscribes to one trip's status and driver location.
 *
 * Returns a disposer. Reconnection is delegated to stompjs' own
 * `reconnectDelay` rather than hand-rolled: the app hand-rolls backoff because
 * it also manages app-background/foreground transitions, which a browser tab
 * does not have.
 */
export function subscribeToTrip(tripId: string, handlers: TripSocketHandlers): () => void {
  let subscriptions: StompSubscription[] = [];
  let disposed = false;

  const client = new Client({
    webSocketFactory: () => new SockJS(sockJsUrl()) as unknown as WebSocket,
    connectHeaders: connectHeaders(),
    heartbeatIncoming: 20000,
    heartbeatOutgoing: 20000,
    reconnectDelay: 5000,
    connectionTimeout: 15000,
    onConnect: () => {
      if (disposed) return;

      subscriptions = [
        client.subscribe(`/user/queue/trip-status.${tripId}`, (message) => {
          const body = parseBody(message);
          if (body) handlers.onTripStatus(body);
        }),
        client.subscribe(`/user/queue/driver-location.${tripId}`, (message) => {
          const body = parseBody(message);
          if (body) handlers.onDriverLocation(body);
        }),
      ];

      handlers.onConnectionChange?.(true);
    },
    onWebSocketClose: () => handlers.onConnectionChange?.(false),
    onStompError: () => handlers.onConnectionChange?.(false),
  });

  client.activate();

  return () => {
    disposed = true;
    subscriptions.forEach((subscription) => {
      try {
        subscription.unsubscribe();
      } catch {
        /* Already gone with the socket. */
      }
    });
    subscriptions = [];
    void client.deactivate();
    handlers.onConnectionChange?.(false);
  };
}

/**
 * Coordinates from a driver-location frame.
 *
 * Publishers disagree about shape — the app documents finding `latitude`,
 * `lat`, `location.*` and `coords.*` in the wild — so both clients must accept
 * all of them. Reading only `latitude` would silently show no driver.
 */
export function readCoordinates(
  payload: Record<string, unknown> | null | undefined,
): { lat: number; lng: number } | null {
  if (!payload) return null;

  const asNumber = (value: unknown): number | null => {
    const parsed = typeof value === 'string' ? Number(value) : value;
    return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
  };

  const nested = (key: 'location' | 'coords') =>
    (payload[key] ?? null) as Record<string, unknown> | null;

  const lat =
    asNumber(payload.latitude) ??
    asNumber(payload.lat) ??
    asNumber(nested('location')?.latitude) ??
    asNumber(nested('location')?.lat) ??
    asNumber(nested('coords')?.latitude) ??
    asNumber(nested('coords')?.lat);

  const lng =
    asNumber(payload.longitude) ??
    asNumber(payload.lng) ??
    asNumber(nested('location')?.longitude) ??
    asNumber(nested('location')?.lng) ??
    asNumber(nested('coords')?.longitude) ??
    asNumber(nested('coords')?.lng);

  return lat != null && lng != null ? { lat, lng } : null;
}

/**
 * Compass heading from a driver-location frame, if the publisher sends one.
 *
 * Same tolerance as `readCoordinates`: the field is variously `heading`,
 * `bearing` or `course`. Normalised into 0–359 so a marker rotation can never
 * be handed a negative or out-of-range angle.
 */
export function readHeading(payload: Record<string, unknown> | null | undefined): number | null {
  if (!payload) return null;

  const candidates = [payload.heading, payload.bearing, payload.course];

  for (const candidate of candidates) {
    const parsed = typeof candidate === 'string' ? Number(candidate) : candidate;
    if (typeof parsed === 'number' && Number.isFinite(parsed)) {
      return ((parsed % 360) + 360) % 360;
    }
  }

  return null;
}
